import React, { useContext } from 'react';
import { Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PropTypes from 'prop-types';

/**
 * A button that links to a BigQuery table in the Google Cloud Console.
 */
const ViewTableInBigQueryButton = ({
  projectId,
  datasetId,
  tableId,
  buttonText = "View in BigQuery",
  variant = "contained",
  color = "primary",
  size = "medium",
  sx = {},
  ...otherProps
}) => {
  // Ensure all required parameters are present
  if (!projectId || !datasetId || !tableId) {
    return null;
  }

  const bigQueryUrl = `https://console.cloud.google.com/bigquery?project=${projectId}&p=${projectId}&d=${datasetId}&t=${tableId}&page=table`;
  
  // Get the onClick handler from props, if any
  const { onClick: existingOnClick } = otherProps;
  
  // Create a new onClick handler that opens the URL and calls the existing handler
  const handleClick = (event) => {
    event.stopPropagation(); // Prevent click from bubbling to parent elements
    window.open(bigQueryUrl, '_blank', 'noopener,noreferrer');
    
    // Call the existing onClick handler if provided
    if (existingOnClick) {
      existingOnClick(event);
    }
  };

  // In the ListCutterJobsIndexPage, we're using onClick to stop propagation,
  // so we'll detect that and render a button instead of a link
  if (otherProps.onClick) {
    return (
      <Button
        variant={variant}
        color={color}
        onClick={handleClick}
        endIcon={<ArrowForwardIcon />}
        sx={{ 
          fontSize: '1rem',
          py: 1.5,
          px: 3,
          borderRadius: 2,
          ...sx
        }}
        {...otherProps}
      >
        {buttonText}
      </Button>
    );
  }

  // Default rendering as a link
  return (
    <Button
      variant={variant}
      color={color}
      href={bigQueryUrl}
      target="_blank"
      rel="noopener noreferrer"
      endIcon={<ArrowForwardIcon />}
      sx={{ 
        fontSize: '1rem',
        py: 1.5,
        px: 3,
        borderRadius: 2,
        ...sx
      }}
      {...otherProps}
    >
      {buttonText}
    </Button>
  );
};

ViewTableInBigQueryButton.propTypes = {
  projectId: PropTypes.string.isRequired,
  datasetId: PropTypes.string.isRequired,
  tableId: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  variant: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  sx: PropTypes.object,
};

export default ViewTableInBigQueryButton;
